/**
 * Google Tag Manager integration for Telegram Mini App.
 *
 * Loads the GTM container script dynamically so it can be skipped
 * in development when VITE_GTM_ID is empty.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

let initialized = false;
let scriptLoaded = false;

/** Max dataLayer entries when GTM script hasn't loaded (prevents memory leak). */
const DATALAYER_CAP = 50;

/**
 * Inject the GTM container script into the page.
 * No-ops when `gtmId` is falsy (e.g. local development).
 */
export function initGTM(gtmId: string | undefined): void {
  if (!gtmId || initialized) return;

  // Validate GTM ID format (GTM-XXXXXX)
  if (!/^GTM-[A-Z0-9]{6,8}$/i.test(gtmId)) {
    console.warn('[GTM] Invalid container ID:', gtmId);
    return;
  }

  initialized = true;

  // Ensure dataLayer exists (also set in index.html as a safety net)
  window.dataLayer = window.dataLayer || [];

  // Standard GTM bootstrap snippet (programmatic version)
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  script.onload = () => {
    scriptLoaded = true;
  };
  script.onerror = () => {
    console.warn('[GTM] Script failed to load — analytics disabled');
    initialized = false;
  };
  document.head.appendChild(script);
}

/**
 * Push a custom event into the GTM dataLayer.
 *
 * @example
 * pushEvent('referral_share', { method: 'telegram' })
 */
export function pushEvent(
  event: string,
  data?: Record<string, unknown>,
): void {
  if (!initialized) return;

  // If GTM script hasn't loaded yet, cap the queue to prevent memory leak
  if (!scriptLoaded && window.dataLayer.length >= DATALAYER_CAP) return;

  // GTM replaces Array.push with its own wrapper after loading.
  // Wrap in try-catch so a GTM internal error never propagates to app code.
  try {
    window.dataLayer.push({ event, ...data });
  } catch {
    // GTM error — silently ignore, game continues
  }
}

/**
 * Push a virtual page-view event.
 * Useful for SPA tab switches that GTM cannot detect automatically.
 */
export function pushPageView(pageName: string): void {
  pushEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href,
  });
}

/**
 * Set the user identity for GA4 user-ID feature.
 * Call once after Telegram initData is available.
 */
export function setGTMUserId(telegramUserId: number): void {
  if (!initialized) return;
  try {
    window.dataLayer.push({ user_id: String(telegramUserId) });
  } catch {
    // GTM error — silently ignore
  }
}
